/**
 * Notification Action Registry
 *
 * Handles interactive notification actions like "Create client" buttons.
 * Actions are registered by name and can be invoked from the notification UI.
 */

import type { InAppNotification } from './types';

/**
 * Action handler function signature
 */
export type NotificationActionHandler = (
  notification: InAppNotification,
  payload: Record<string, unknown>
) => Promise<{ success: boolean; message?: string; data?: unknown }>;

/** Registry of action handlers */
const actionHandlers: Map<string, NotificationActionHandler> = new Map();

/**
 * Register a notification action handler
 */
export function registerNotificationAction(name: string, handler: NotificationActionHandler): void {
  actionHandlers.set(name, handler);
}

/**
 * Unregister a notification action handler
 */
export function unregisterNotificationAction(name: string): void {
  actionHandlers.delete(name);
}

/**
 * Get all registered action handler names
 */
export function getRegisteredActions(): string[] {
  return Array.from(actionHandlers.keys());
}

/**
 * Execute a notification action
 */
export async function executeNotificationAction(
  actionName: string,
  notification: InAppNotification,
  payload: Record<string, unknown>
): Promise<{ success: boolean; message?: string; data?: unknown }> {
  const handler = actionHandlers.get(actionName);
  if (!handler) {
    return { success: false, message: `Action handler "${actionName}" not found` };
  }

  try {
    return await handler(notification, payload);
  } catch (err) {
    return {
      success: false,
      message: err instanceof Error ? err.message : 'Action failed',
    };
  }
}

// =============================================================================
// BUILT-IN ACTION HANDLERS
// =============================================================================

/**
 * Create a new client/contact from email sender info
 */
registerNotificationAction('createClientFromEmail', async (notification, payload) => {
  const { useHubStore } = await import('../../stores/hubStore');
  const store = useHubStore.getState();

  const email = payload.email as string;
  const name = payload.name as string;
  const company = payload.company as string | undefined;

  if (!email || !name) {
    return { success: false, message: 'Email e nome sono richiesti' };
  }

  try {
    const contactId = await store.createContact({
      displayName: name,
      handles: { email },
      company: company || undefined,
      tags: ['da-email'],
      notes: `Creato automaticamente da email ricevuta il ${new Date().toLocaleDateString('it-IT')}`,
    });

    return {
      success: true,
      message: `Cliente "${name}" creato con successo!`,
      data: { contactId },
    };
  } catch (err) {
    return {
      success: false,
      message: err instanceof Error ? err.message : 'Errore nella creazione del cliente',
    };
  }
});

/**
 * Mark email as spam (for future filtering)
 */
registerNotificationAction('markAsSpam', async (notification, payload) => {
  const email = payload.senderEmail as string;
  // TODO: Implement spam list storage
  console.log(`[NotificationAction] Marked as spam: ${email}`);
  return { success: true, message: 'Email segnalata come spam' };
});

/**
 * Open email in detail view
 */
registerNotificationAction('openEmail', async (notification, payload) => {
  const emailId = payload.emailId as string;
  // This will be handled by the UI to navigate
  return { success: true, data: { navigate: `/hub/email/${emailId}` } };
});

/**
 * Dismiss notification (just acknowledge)
 */
registerNotificationAction('dismiss', async () => {
  return { success: true };
});
